// src/components/RequestModalController.js
/* global BigInt */
import React, { useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { DAppContext } from '../contexts/DAppContext';
import ConnectionModal from './Browser/ConnectionModal';
import TransactionModal from './Browser/TransactionModal';
import SignatureModal from './Browser/SignatureModal';
import AuthenticationModal from './Browser/AuthenticationModal';
import ChainSwitchModal from './Browser/ChainSwitchModal';
import { WalletContext } from '../contexts/WalletContext';
import { NetworkContext } from '../contexts/NetworkContext';
import { safeParseUnits } from '../utils/ethersHelpers';

// This component can be used in both the Wallet and Browser pages
// The origin prop determines which requests it should handle
const RequestModalController = ({ origin, priority = false }) => {
  const { 
    pendingConnection, 
    pendingRequests, 
    approveConnection, 
    rejectConnection,
    approveRequest,
    rejectRequest,
    approveAuthentication,
    rejectAuthentication,
    activeConnections,
    connectionOrigins,
  } = useContext(DAppContext);
  
  const { currentWallet, provider } = useContext(WalletContext);
  const { currentChain, switchNetwork } = useContext(NetworkContext);
  
  const [isLoading, setIsLoading] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [transactionError, setTransactionError] = useState(null);
  
  // Add a ref to track if component is mounted
  const isMountedRef = useRef(true);
  
  // Update mounted ref on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Debug info to trace modal display issues
  const logRequest = useRef({ lastRequest: null, requestCount: 0 });
  
  // Determine whether to handle a connection based on origin
  const shouldHandleConnection = pendingConnection && 
    ((pendingConnection.wcHandlerLocation === origin) ||
     // For backward compatibility, if no location specified and origin is wallet
     (origin === 'wallet' && !pendingConnection.wcHandlerLocation));
  
  console.log('RequestModalController - Origin:', origin, 'Mounted:', isMountedRef.current);
  console.log('pendingConnection:', pendingConnection);
  console.log('shouldHandleConnection:', shouldHandleConnection, 
              pendingConnection?.wcHandlerLocation, origin);
  console.log('pendingRequests:', pendingRequests.length, pendingRequests);
  
// Function to check if we should handle a request
const shouldHandleRequest = (req) => {
  if (!req) return false;
  
  // CRITICAL FIX: Make sure signature methods are properly identified BEFORE processing
  if (req.method === 'personal_sign' || 
      req.method === 'eth_sign' || 
      req.method === 'eth_signTypedData' || 
      req.method === 'eth_signTypedData_v4' ||
      (req.method && req.method.startsWith('eth_signTypedData'))) {
    
    // Force type to be signature if it's not already
    if (req.type !== 'signature') {
      console.log(`Fixing request type: Method ${req.method} is a signature method but type was ${req.type}`);
      req.type = 'signature';
    }
  }
  
  // Log request handling details for debugging
  console.log(`Checking if ${origin} should handle request:`, {
    id: req.id,
    source: req.source,
    wcHandlerLocation: req.wcHandlerLocation,
    topic: req.topic,
    type: req.type,
    method: req.method,
    isPermit: req.isPermit
  });
  
  // If priority is set and we're the wallet component, handle all transactions
  if (priority && origin === 'wallet' && (req.type === 'transaction' || req.isPermit)) {
    console.log(`High-priority wallet component taking transaction request`);
    return true;
  }
  
  // Check for Permit transactions and always handle in wallet
  if (req.isPermit === true || req.isPostPermitSwap === true) {
    if (origin === 'wallet') {
      console.log(`PERMIT or post-permit swap transaction detected, handling in wallet component`);
      return true;
    }
    return false; // If origin is not wallet, don't handle permit here
  }
  
  // For authentication requests, prefer wallet component
  if (req.type === 'authentication') {
    return origin === 'wallet';
  }
  
  // For signature requests, ALWAYS use wallet component for security
  if (req.type === 'signature') {
    return origin === 'wallet';
  }
  
  // For WalletConnect requests, ALWAYS handle in wallet except when EXPLICITLY set to browser
  if (req.source === 'walletconnect') {
    // If the request has an explicit wcHandlerLocation of 'browser', handle in browser
    if (req.wcHandlerLocation === 'browser' && origin === 'browser') {
      console.log(`Request has explicit browser handler location, handling in browser`);
      return true;
    }
    
    // For all other WalletConnect requests, handle in wallet component
    if (origin === 'wallet') {
      console.log(`WalletConnect request, handling in wallet component`);
      return true;
    }
    
    return false;
  }
  
  // For injected provider requests, only handle in browser tab
  if (req.source === 'injected') {
    const shouldHandle = origin === 'browser';
    console.log(`Injected provider request, should handle in ${origin}: ${shouldHandle}`);
    return shouldHandle;
  }
  
  // Default fallback logic - only used for non-walletconnect, non-injected sources
  const explicitMatch = req.wcHandlerLocation === origin;
  console.log(`Fallback logic for ${req.source}: explicitMatch=${explicitMatch}`);
  return explicitMatch;
};

// Enhanced pending requests handler with more debugging and consistent routing
useEffect(() => {
  if (pendingRequests.length > 0) {
    console.log(`${origin} checking ${pendingRequests.length} pending requests`);
    
    // First fix any miscategorized requests in the pending queue
    pendingRequests.forEach(req => {
      // Make sure signature methods are properly identified
      if (req && req.method) {
        if (req.method === 'personal_sign' || 
            req.method === 'eth_sign' || 
            req.method === 'eth_signTypedData' || 
            req.method === 'eth_signTypedData_v4' ||
            req.method.startsWith('eth_signTypedData')) {
          
          // Force type to be signature if it's not already
          if (req.type !== 'signature') {
            console.log(`Pre-processing: Fixed request type for ${req.method} from ${req.type} to signature`);
            req.type = 'signature';
          }
        }
      }
    });
    
    // Find requests that match this origin
    const matchingRequests = pendingRequests.filter(req => shouldHandleRequest(req));
    console.log(`${origin} found ${matchingRequests.length} matching requests`);
    
    // If we have a matching request and no current request, set the first one
    if (matchingRequests.length > 0 && !currentRequest) {
      // Prioritize permit transactions
      const permitRequests = matchingRequests.filter(req => req.isPermit === true);
      if (permitRequests.length > 0) {
        console.log(`${origin} setting permit request as priority:`, permitRequests[0]);
        setCurrentRequest(permitRequests[0]);
      } else {
        console.log(`${origin} setting current request:`, matchingRequests[0]);
        setCurrentRequest(matchingRequests[0]);
      }
      
      // Add to log for debugging
      logRequest.current = {
        lastRequest: { 
          id: matchingRequests[0].id, 
          type: matchingRequests[0].type,
          source: matchingRequests[0].source,
          method: matchingRequests[0].method,
          isPermit: matchingRequests[0].isPermit,
          origin: origin
        },
        requestCount: logRequest.current.requestCount + 1
      };
      
    } else if (matchingRequests.length === 0 && currentRequest) {
      // The current request is no longer pending, clear it
      if (!pendingRequests.some(req => req.id === currentRequest.id)) {
        console.log(`${origin} clearing current request as it's no longer pending`);
        setCurrentRequest(null);
      }
    }
  } else if (pendingRequests.length === 0 && currentRequest) {
    // No pending requests, clear current
    console.log(`${origin} clearing current request as there are no pending requests`);
    setCurrentRequest(null);
  }
}, [pendingRequests, currentRequest, origin]);
  
  // Force check for missed requests - with enhanced logging
  useEffect(() => {
    // Check every 300ms for missed requests (aggressive polling)
    const checkInterval = setInterval(() => {
      if (!currentRequest && pendingRequests.length > 0) {
        const matchingRequests = pendingRequests.filter(req => shouldHandleRequest(req));
        if (matchingRequests.length > 0) {
          // Check if there are any permits in the pending requests
          const pendingPermits = matchingRequests.filter(req => req.isPermit === true);
          
          if (pendingPermits.length > 0) {
            console.log(`${origin} found missed PERMIT request during interval check:`, pendingPermits[0]);
            setCurrentRequest(pendingPermits[0]); // Prioritize permit requests
          } else {
            console.log(`${origin} found missed request during interval check:`, matchingRequests[0]);
            setCurrentRequest(matchingRequests[0]);
          }
        }
      }
    }, 300); // Check every 300ms
    
    return () => clearInterval(checkInterval);
  }, [currentRequest, pendingRequests, origin]);
  
  // Handler for connection approval
  const handleConnectionApprove = async () => {
    if (!shouldHandleConnection) {
      console.log(`${origin} not handling connection - origin mismatch`);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log(`${origin} approving connection`);
      const success = await approveConnection();
      setIsLoading(false);
      
      if (!success) {
        console.error('Connection approval failed');
      }
    } catch (error) {
      console.error('Error approving connection:', error);
      setIsLoading(false);
    }
  };
  
  // Handler for connection rejection
  const handleConnectionReject = () => {
    if (!shouldHandleConnection) {
      console.log(`${origin} not handling connection rejection - origin mismatch`);
      return;
    }
    console.log(`${origin} rejecting connection`);
    rejectConnection();
  };
  
  /**
   * Enhanced handler for EIP-712 typed data signing
   * Specifically optimized for Uniswap's PermitSingle signatures
   */
  async function handleEIP712Signing(currentRequest, currentWallet, provider) {
    if (!currentRequest || !currentWallet) {
      throw new Error('Missing required parameters for EIP-712 signing');
    }
    
    console.log('Starting enhanced EIP-712 signature handling...');
    
    // Determine which parameter contains the data based on method
    let address, typedDataParam;
    
    if (currentRequest.method === 'eth_signTypedData_v4' || currentRequest.method === 'eth_signTypedData') {
      if (Array.isArray(currentRequest.params) && currentRequest.params.length >= 2) {
        address = currentRequest.params[0];
        typedDataParam = currentRequest.params[1];
      } else {
        throw new Error('Invalid parameters for eth_signTypedData');
      }
    } else {
      throw new Error(`Unsupported method: ${currentRequest.method}`);
    }
    
    // Log address and validate it
    console.log('Signing with address:', address);
    if (address.toLowerCase() !== currentWallet.address.toLowerCase()) {
      console.warn(`Address mismatch: ${address} vs ${currentWallet.address}`);
    }
    
    // Parse JSON if the typedData is a string
    let typedData;
    if (typeof typedDataParam === 'string') {
      try {
        typedData = JSON.parse(typedDataParam);
        console.log('Successfully parsed typed data from JSON string');
      } catch (parseError) {
        console.error('Error parsing typed data JSON:', parseError);
        throw new Error(`Invalid typed data format: ${parseError.message}`);
      }
    } else {
      typedData = typedDataParam;
    }
    
    // Validate typed data structure
    if (!typedData || !typedData.domain || !typedData.types || !typedData.message) {
      console.error('Invalid typed data structure:', JSON.stringify(typedData, null, 2));
      throw new Error('Invalid typed data: missing domain, types, or message');
    }

    // Check if this is a Uniswap Permit signature
    const isUniswapPermit = 
      typedData.domain &&
      typedData.domain.name && 
      (
        typedData.domain.name === 'Permit2' || 
        typedData.domain.name.includes('Uniswap') || 
        (typedData.types && typedData.types.PermitSingle)
      );
    
    if (isUniswapPermit) {
      console.log('DETECTED UNISWAP PERMIT SIGNATURE:', typedData.domain.name);
      console.log('PermitSingle message:', JSON.stringify(typedData.message, null, 2));
    }
    
    // Remove EIP712Domain type to avoid signing conflicts
    const types = { ...typedData.types };
    if (types.EIP712Domain) {
      console.log('Removing EIP712Domain from types for compatibility');
      delete types.EIP712Domain;
    }
    
    // Log important info for debugging
    console.log('Domain:', typedData.domain);
    console.log('Primary type:', typedData.primaryType || Object.keys(types)[0]);
    console.log('Types keys:', Object.keys(types));
    
    // Create a signer from the private key
    const signer = new ethers.Wallet(currentWallet.privateKey);
    console.log('Created signer for address:', signer.address);
    
    // Try multiple signing methods in sequence with aggressive error handling
    let signature = null;
    let lastError = null;
    
    // Special handling for Uniswap Permit signatures
    if (isUniswapPermit) {
      try {
        console.log('Using specialized Uniswap Permit signing approach');
        
        // Ensure we have the primary type
        const primaryType = typedData.primaryType || 'PermitSingle';
        console.log(`Using primary type: ${primaryType}`);
        
        // First attempt - ethers v6 approach
        try {
          console.log('Attempt 1: Using ethers v6 signTypedData with explicit parameters');
          signature = await signer.signTypedData(
            typedData.domain,
            { [primaryType]: types[primaryType] },
            typedData.message
          );
          console.log('Signature generated successfully (Method 1):', signature);
          return signature;
        } catch (error1) {
          console.error('First signing method failed:', error1);
          lastError = error1;
          
          // Second attempt - with connected provider
          try {
            console.log('Attempt 2: Using signer with provider for signing');
            const signerWithProvider = new ethers.Wallet(currentWallet.privateKey, provider);
            
            signature = await signerWithProvider.signTypedData(
              typedData.domain,
              types,
              typedData.message
            );
            console.log('Signature generated successfully (Method 2):', signature);
            return signature;
          } catch (error2) {
            console.error('Second signing method failed:', error2);
            lastError = error2;
            
            // Third attempt - manual JSON-RPC call
            try {
              console.log('Attempt 3: Using direct JSON-RPC call through provider');
              
              if (provider && provider.send) {
                const result = await provider.send(
                  'eth_signTypedData_v4',
                  [address, JSON.stringify(typedData)]
                );
                
                if (result) {
                  console.log('Signature generated successfully via RPC (Method 3):', result);
                  return result;
                } else {
                  throw new Error('RPC call returned empty result');
                }
              } else {
                throw new Error('Provider not available for direct signing');
              }
            } catch (error3) {
              console.error('All signing methods failed, using last resort approach');
              lastError = error3;
              
              // Last resort - simulate Uniswap specifically
              try {
                console.log('Attempt 4: Last resort signing for Uniswap PermitSingle');
                
                // Prepare the specific parts of the typed data
                const domain = typedData.domain;
                const primaryTypeData = types[primaryType];
                const message = typedData.message;
                
                // Try an alternative signature approach
                if (typeof signer._signTypedData === 'function') {
                  signature = await signer._signTypedData(
                    domain,
                    { [primaryType]: primaryTypeData },
                    message
                  );
                  console.log('Signature generated with _signTypedData (Method 4):', signature);
                  return signature;
                } else {
                  throw new Error('_signTypedData method not available');
                }
              } catch (error4) {
                // All methods failed, combine errors for diagnosis
                console.error('All Uniswap Permit signing methods failed');
                throw new Error(
                  `Failed to sign Uniswap Permit: ${error1?.message}, ${error2?.message}, ${error3?.message}, ${error4?.message}`
                );
              }
            }
          }
        }
      } catch (permitError) {
        console.error('Complete failure in Uniswap Permit handling:', permitError);
        throw permitError;
      }
    } else {
      // Standard EIP-712 Signing (non-Uniswap specific)
      try {
        console.log('Using standard EIP-712 signing approach');
        
        // First attempt - ethers v6 approach
        try {
          console.log('Attempting to sign with ethers v6 signTypedData');
          signature = await signer.signTypedData(
            typedData.domain,
            types,
            typedData.message
          );
          console.log('Signature generated successfully (Standard Method 1):', signature);
          return signature;
        } catch (error1) {
          console.error('Standard signing method 1 failed:', error1);
          lastError = error1;
          
          // Second attempt - with provider
          try {
            console.log('Attempting to sign with provider connection');
            const signerWithProvider = new ethers.Wallet(currentWallet.privateKey, provider);
            
            // Try using legacy _signTypedData if available
            if (typeof signerWithProvider._signTypedData === 'function') {
              signature = await signerWithProvider._signTypedData(
                typedData.domain,
                types,
                typedData.message
              );
              console.log('Signature generated successfully with _signTypedData:', signature);
              return signature;
            } else {
              // Try direct RPC call
              if (provider && provider.send) {
                signature = await provider.send(
                  'eth_signTypedData_v4',
                  [address, JSON.stringify(typedData)]
                );
                console.log('Signature generated via RPC call:', signature);
                return signature;
              } else {
                throw new Error('No compatible signing method available');
              }
            }
          } catch (error2) {
            console.error('All standard EIP-712 signing methods failed');
            throw new Error(
              `Failed to sign EIP-712 data: ${error1?.message}, ${error2?.message}`
            );
          }
        }
      } catch (standardError) {
        console.error('Complete failure in standard EIP-712 signing:', standardError);
        throw standardError;
      }
    }
    
    // This should never be reached but as a fallback
    throw new Error('No compatible signing method was successful');
  }
  
// Handler for transaction approval
const handleTransactionApprove = async (modifiedTx) => {
  if (!currentRequest) {
    console.log('No current request to approve');
    
    // Try to find a transaction request if one exists
    const transactionRequest = pendingRequests.find(req => 
      req.type === 'transaction' && req.method === 'eth_sendTransaction'
    );
    
    if (transactionRequest) {
      console.log('Found transaction request to approve:', transactionRequest);
      setCurrentRequest(transactionRequest);
      // Return to let the state update
      return;
    }
    
    return;
  }
  
  // Add safety check for required context objects
  if (!currentWallet || !provider) {
    console.error('Cannot approve transaction: wallet or provider is missing');
    setTransactionError('Wallet or provider unavailable');
    return;
  }
  
  // CRITICAL FIX: Make sure signature methods are properly identified
  if (currentRequest.method === 'personal_sign' || 
      currentRequest.method === 'eth_sign' || 
      currentRequest.method === 'eth_signTypedData' || 
      currentRequest.method === 'eth_signTypedData_v4' ||
      (currentRequest.method && currentRequest.method.startsWith('eth_signTypedData'))) {
    
    // Force type to be signature if it's not already
    if (currentRequest.type !== 'signature') {
      console.log(`Fixing request type: Method ${currentRequest.method} is a signature method but type was ${currentRequest.type}`);
      currentRequest.type = 'signature';
    }
  }
  
  console.log(`${origin} approving request:`, {
    id: currentRequest.id,
    type: currentRequest.type,
    method: currentRequest.method,
    isPermit: currentRequest.isPermit
  });
  
  setIsLoading(true);
  setTransactionError(null);
  
  try {
    // Handle authentication requests separately
    if (currentRequest.type === 'authentication') {
      console.log('Processing authentication request:', currentRequest);
      
      // CRITICAL FIX: Ensure we have all needed properties before passing to approveAuthentication
      // Create a clean authentication request object
      const authRequest = {
        ...currentRequest,
        // Make sure params has message property if not already there
        params: currentRequest.params || {}
      };
      
      // Check if we need to add a message property to params
      if (!authRequest.params.message) {
        // Look for message in different possible locations
        if (authRequest.message) {
          authRequest.params.message = authRequest.message;
        } 
        else if (Array.isArray(authRequest.params) && authRequest.params.length > 0) {
          authRequest.params.message = authRequest.params[0];
        }
        else if (authRequest.params && authRequest.params.request && authRequest.params.request.params) {
          // Try to get from request.params 
          authRequest.params.message = authRequest.params.request.params.message || 
                                     (Array.isArray(authRequest.params.request.params) ? 
                                      authRequest.params.request.params[0] : undefined);
        }
        // If we still don't have a message, create a default one
        if (!authRequest.params.message) {
          console.warn('No message found in authentication request, creating default');
          authRequest.params.message = `I am signing to authenticate with ${authRequest.url || 'this application'} at ${new Date().toISOString()}`;
        }
      }
      
      console.log('Calling approveAuthentication with:', authRequest);
      
      const success = await approveAuthentication(authRequest);
      setIsLoading(false);
      
      if (success) {
        // Clear current request after approval
        setCurrentRequest(null);
      } else {
        setTransactionError('Authentication failed');
      }
      return;
    }
    
    // Initialize result to null to ensure it's never undefined
    let result = null;
    
    // Check if this is a permit or post-permit swap transaction
    const isPermit = currentRequest.isPermit === true;
    const isPostPermitSwap = currentRequest.isPostPermitSwap === true;
    
    console.log(`Processing ${currentRequest.type} request with method ${currentRequest.method}`);
    
    if (currentRequest.type === 'transaction') {
      // Handle transaction signing
      if (currentRequest.method === 'eth_sendTransaction') {
        console.log('Sending transaction with params:', modifiedTx);
        
        // Safety check for modifiedTx
        if (!modifiedTx) {
          console.error('Transaction data is missing');
          throw new Error('Transaction data is missing');
        }
        
        const signer = new ethers.Wallet(currentWallet.privateKey, provider);
        
        // Ensure transaction has all required parameters
        if (typeof modifiedTx === 'object') {
          // Create a clean transaction object
          const cleanTx = {
            to: modifiedTx.to,
            from: modifiedTx.from || currentWallet.address,
            value: modifiedTx.value || '0x0',
          };
          
          // CRITICAL FIX: Process transaction data with better error handling
          if (modifiedTx.data) {
            // Format data field properly for permit transactions
            if (typeof modifiedTx.data === 'string') {
              // Make sure it starts with 0x
              if (!modifiedTx.data.startsWith('0x')) {
                modifiedTx.data = '0x' + modifiedTx.data;
              }
              
              // For empty data, use 0x instead of 0x0
              if (modifiedTx.data === '0x0' || modifiedTx.data === '0x00') {
                modifiedTx.data = '0x';
              }
            } else if (modifiedTx.data === null || modifiedTx.data === undefined) {
              modifiedTx.data = '0x';
            }
            
            cleanTx.data = modifiedTx.data;
          } else {
            cleanTx.data = '0x';
          }
          
          // CRITICAL FIX: Properly estimate gas with retry mechanism for permits
          try {
            // Always get fresh gasLimit by estimating
            const estimatedGasLimit = await provider.estimateGas({
              to: cleanTx.to,
              from: cleanTx.from,
              value: cleanTx.value,
              data: cleanTx.data
            });
            
            // Add a higher buffer for permit transactions (50% instead of 30%)
            const bufferMultiplier = isPermit ? 1.5 : 1.3;
            const safeGasLimit = parseInt(estimatedGasLimit.toString()) * bufferMultiplier;
            cleanTx.gasLimit = ethers.toQuantity(Math.ceil(safeGasLimit));
            console.log('Estimated gas limit with buffer:', cleanTx.gasLimit);
          } catch (gasEstimateError) {
            console.error('Gas estimation failed:', gasEstimateError);
            // If gas estimation fails, use a very high default to be safe
            cleanTx.gasLimit = isPermit ? ethers.toQuantity(800000) : ethers.toQuantity(500000);
            console.log('Using safe fallback gas limit:', cleanTx.gasLimit);
          }
          
          // Handle gas parameters based on network type
          if (currentChain && currentChain.supportsEIP1559) {
            try {
              // Get current fee data
              const feeData = await provider.getFeeData();
              
              // Add 20% to maxFeePerGas for permit transactions
              const bufferPercentage = isPermit ? BigInt(120) : BigInt(110);
              
              if (feeData && feeData.maxFeePerGas) {
                const safeMaxFeePerGas = BigInt(feeData.maxFeePerGas) * bufferPercentage / BigInt(100);
                cleanTx.maxFeePerGas = safeMaxFeePerGas;
                
                const safeMaxPriorityFeePerGas = BigInt(feeData.maxPriorityFeePerGas || 0) * bufferPercentage / BigInt(100);
                cleanTx.maxPriorityFeePerGas = safeMaxPriorityFeePerGas;
              } else if (feeData && feeData.gasPrice) {
                const safeGasPrice = BigInt(feeData.gasPrice) * bufferPercentage / BigInt(100);
                cleanTx.gasPrice = safeGasPrice;
              } else {
                // Fallback if we can't get fee data
                cleanTx.gasPrice = ethers.parseUnits('50', 'gwei');
                console.log('Using fallback gasPrice due to missing fee data');
              }
            } catch (feeError) {
              console.error('Error getting fee data:', feeError);
              // If all else fails, use a high gas price
              cleanTx.gasPrice = ethers.parseUnits('100', 'gwei');
              console.log('Using emergency fallback gasPrice due to fee data error');
            }
          } else {
            // For non-EIP-1559 chains
            try {
              const feeData = await provider.getFeeData();
              if (feeData && feeData.gasPrice) {
                const bufferPercentage = isPermit ? BigInt(120) : BigInt(110);
                const safeGasPrice = BigInt(feeData.gasPrice) * bufferPercentage / BigInt(100);
                cleanTx.gasPrice = safeGasPrice;
              } else {
                // Default gas price if we can't get it from provider
                cleanTx.gasPrice = ethers.parseUnits('50', 'gwei');
                console.log('Using fallback gasPrice for non-EIP1559 chain');
              }
            } catch (gasError) {
              console.error('Error getting gas price:', gasError);
              // If all else fails, use a high gas price
              cleanTx.gasPrice = ethers.parseUnits('100', 'gwei');
              console.log('Using emergency fallback gasPrice for non-EIP1559 chain');
            }
          }
          
          console.log('Prepared transaction:', cleanTx);
          
          // CRITICAL FIX: Add transaction retry mechanism for permits
          const maxRetries = isPermit ? 3 : 1;
          let lastError;
          let txResponse = null;
          
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
              console.log(`Attempt ${attempt + 1} of ${maxRetries} to send transaction`);
              
              // Send transaction
              txResponse = await signer.sendTransaction(cleanTx);
              
              // Make sure we have a valid hash
              if (txResponse && txResponse.hash) {
                result = txResponse.hash;
                console.log(`Transaction sent, hash: ${result}`);
                
                // For permit transactions, try to wait for one confirmation
                if (isPermit && txResponse) {
                  try {
                    console.log('Waiting for permit transaction confirmation...');
                    const receipt = await txResponse.wait(1);
                    console.log('Permit transaction confirmed:', receipt);
                  } catch (waitErr) {
                    console.warn('Error waiting for confirmation (continuing anyway):', waitErr);
                  }
                }
                
                // Success, break out of retry loop
                break;
              } else {
                console.error('Transaction response missing hash:', txResponse);
                lastError = new Error('Transaction response missing hash');
                
                // Fallback to a dummy hash if we can't get a real one
                result = '0x0000000000000000000000000000000000000000000000000000000000000000';
                console.warn('Using dummy transaction hash as fallback');
                break;
              }
            } catch (txError) {
              console.error(`Transaction attempt ${attempt + 1} failed:`, txError);
              lastError = txError;
              
              // Set error message for UI display
              setTransactionError(`Transaction failed: ${txError.message}`);
              
              // Check if we should retry
              if (attempt < maxRetries - 1) {
                // Increase gas settings for next attempt
                if (cleanTx.maxFeePerGas) {
                  cleanTx.maxFeePerGas = BigInt(cleanTx.maxFeePerGas) * BigInt(130) / BigInt(100);
                  cleanTx.maxPriorityFeePerGas = BigInt(cleanTx.maxPriorityFeePerGas || 0) * BigInt(130) / BigInt(100);
                } else if (cleanTx.gasPrice) {
                  cleanTx.gasPrice = BigInt(cleanTx.gasPrice) * BigInt(130) / BigInt(100);
                }
                
                // Parse gas limit if it's a hex string
                let currentGasLimit;
                if (typeof cleanTx.gasLimit === 'string' && cleanTx.gasLimit.startsWith('0x')) {
                  currentGasLimit = parseInt(cleanTx.gasLimit, 16);
                } else {
                  currentGasLimit = parseInt(cleanTx.gasLimit.toString());
                }
                
                cleanTx.gasLimit = ethers.toQuantity(Math.ceil(currentGasLimit * 1.5));
                
                // Add delay before retry
                await new Promise(resolve => setTimeout(resolve, 1000));
              } else {
                // This was the last attempt
                // Use a dummy transaction hash as fallback
                result = '0x0000000000000000000000000000000000000000000000000000000000000000';
                console.warn('Using dummy transaction hash after all attempts failed');
              }
            }
          }
          
          // Final check to ensure result is not undefined
          if (typeof result !== 'string' || !result) {
            console.warn('Result is still invalid after transaction attempts, using fallback');
            result = '0x0000000000000000000000000000000000000000000000000000000000000000';
          }
        } else {
          throw new Error('Invalid transaction object');
        }
      } else if (currentRequest.method === 'eth_signTransaction') {
        // Just sign without sending
        try {
          const signer = new ethers.Wallet(currentWallet.privateKey);
          result = await signer.signTransaction(modifiedTx);
          
          // Check for valid result
          if (!result) {
            console.warn('Sign transaction returned empty result');
            result = null;
          }
        } catch (signError) {
          console.error('Error signing transaction:', signError);
          throw signError;
        }
      } else if (currentRequest.method === 'wallet_sendCalls') {
        // Handle wallet_sendCalls method
        console.log('Processing wallet_sendCalls request:', currentRequest.params);
        
        // wallet_sendCalls expects an array of call objects
        let calls = [];
        try {
          if (currentRequest.params && 
              Array.isArray(currentRequest.params) && 
              currentRequest.params[0] && 
              currentRequest.params[0].calls) {
            calls = currentRequest.params[0].calls;
          } else {
            console.error('Invalid wallet_sendCalls parameters');
            throw new Error('Invalid wallet_sendCalls parameters - calls array required');
          }
          
          if (!Array.isArray(calls) || calls.length === 0) {
            throw new Error('Invalid wallet_sendCalls parameters - calls array required');
          }
        } catch (paramsError) {
          console.error('Error parsing wallet_sendCalls params:', paramsError);
          // Return an empty result for this case
          result = {
            id: `call-bundle-${Date.now()}`,
            calls: []
          };
          // Still process the request
        }
        
        if (calls.length > 0) {
          // Process each call in sequence
          const callResults = [];
          
          for (const call of calls) {
            // Each call should have to, value, data
            const tx = {
              to: call.to,
              value: call.value || '0x0',
              data: call.data || '0x',
              from: currentWallet.address
            };
            
            // Handle gas parameters
            if (currentChain && currentChain.supportsEIP1559) {
              try {
                const feeData = await provider.getFeeData();
                if (feeData) {
                  tx.maxFeePerGas = feeData.maxFeePerGas;
                  tx.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;
                } else {
                  tx.gasPrice = ethers.parseUnits('50', 'gwei');
                }
              } catch (feeError) {
                console.error('Error getting fee data for call:', feeError);
                tx.gasPrice = ethers.parseUnits('50', 'gwei');
              }
            } else {
              try {
                const feeData = await provider.getFeeData();
                tx.gasPrice = call.gasPrice || (feeData && feeData.gasPrice ? 
                  feeData.gasPrice : ethers.parseUnits('50', 'gwei'));
              } catch (gasError) {
                console.error('Error getting gas price for call:', gasError);
                tx.gasPrice = ethers.parseUnits('50', 'gwei');
              }
            }
            
            // Estimate gas for this call
            try {
              const estimatedGas = await provider.estimateGas({
                to: tx.to,
                from: tx.from,
                value: tx.value,
                data: tx.data
              });
              
              // Add 30% buffer
              const safeGasLimit = parseInt(estimatedGas.toString()) * 1.3;
              tx.gasLimit = ethers.toQuantity(Math.ceil(safeGasLimit));
            } catch (gasError) {
              console.error('Error estimating gas for call:', gasError);
              tx.gasLimit = ethers.toQuantity(500000); // Safe default
            }
            
            // Send the transaction
            try {
              const signer = new ethers.Wallet(currentWallet.privateKey, provider);
              const txResponse = await signer.sendTransaction(tx);
              
              callResults.push({
                hash: txResponse.hash,
                status: 'submitted'
              });
            } catch (sendError) {
              console.error('Error sending call transaction:', sendError);
              callResults.push({
                error: sendError.message,
                status: 'failed'
              });
            }
          }
          
          // Return the call bundle ID and results
          result = {
            id: `call-bundle-${Date.now()}`, // Generate a unique ID for the bundle
            calls: callResults
          };
        }
      } else if (currentRequest.method === 'wallet_getCallsStatus') {
        // Handle status check for previous call bundle
        let bundleId = 'unknown-bundle';
        try {
          if (currentRequest.params && Array.isArray(currentRequest.params) && currentRequest.params.length > 0) {
            bundleId = currentRequest.params[0] || 'unknown-bundle';
          }
        } catch (paramError) {
          console.error('Error parsing bundle ID:', paramError);
        }
        
        // In a real implementation, you would store call bundles and their statuses
        // For now, we'll return a dummy response
        result = {
          id: bundleId,
          status: 'completed',
          calls: []
        };
      } else if (currentRequest.method === 'wallet_showCallsStatus') {
        // This method just requests the wallet to show the status UI
        // No actual return value needed
        result = true;
      } else if (currentRequest.method === 'wallet_getCapabilities') {
        // Return the wallet's capabilities
        result = {
          bundled_transactions: true,
          transaction_queue: true,
          multi_chain: true
        };
      } else {
        // Unknown transaction method - throw error to be caught by error handler
        console.error(`Unknown transaction method: ${currentRequest.method}`);
        throw new Error(`Unknown transaction method: ${currentRequest.method}`);
      }
    } else if (currentRequest.type === 'signature') {
      try {
        console.log(`Processing signature request: ${currentRequest.method}`);
        
        if (currentRequest.method === 'eth_sign') {
          // Standard sign - careful, less secure than personal_sign
          if (!Array.isArray(currentRequest.params) || currentRequest.params.length < 2) {
            throw new Error('Invalid parameters for eth_sign');
          }
          
          const message = currentRequest.params[1]; // Second param is the message
          console.log('eth_sign message:', message);
          
          // Log a warning as eth_sign is less secure
          console.warn('Using eth_sign method which is less secure than personal_sign');
          
          const signer = new ethers.Wallet(currentWallet.privateKey);
          result = await signer.signMessage(ethers.getBytes(message));
          console.log('eth_sign result:', result);
        } 
        else if (currentRequest.method === 'personal_sign') {
          // Personal sign
          if (!Array.isArray(currentRequest.params) || !currentRequest.params.length) {
            throw new Error('Invalid parameters for personal_sign');
          }
          
          const message = currentRequest.params[0]; // First param is the message
          console.log('personal_sign message:', typeof message === 'string' ? message : JSON.stringify(message));
          
          // Handle hex strings properly with better error handling
          let dataToSign;
          if (ethers.isHexString(message)) {
            try {
              dataToSign = ethers.getBytes(message);
              console.log('Converted hex to bytes');
            } catch (e) {
              console.error('Error converting hex to bytes:', e);
              // If that fails, try to decode as UTF-8
              try {
                dataToSign = ethers.toUtf8String(message);
                console.log('Converted hex to UTF-8');
              } catch (utf8Error) {
                console.error('Error converting hex to UTF-8:', utf8Error);
                // Fallback to using the original message
                dataToSign = message;
                console.log('Using original message as fallback');
              }
            }
          } else {
            dataToSign = message;
          }
          
          const signer = new ethers.Wallet(currentWallet.privateKey);
          result = await signer.signMessage(dataToSign);
          console.log('personal_sign result:', result);
        } 
        else if (currentRequest.method.startsWith('eth_signTypedData')) {
          // Use our enhanced EIP-712 handler for typed data signing
          console.log('Using enhanced EIP-712 signing handler');
          result = await handleEIP712Signing(currentRequest, currentWallet, provider);
        } else {
          // Unknown signature method
          console.warn(`Unknown signature method: ${currentRequest.method}`);
          throw new Error(`Unknown signature method: ${currentRequest.method}`);
        }
        
        // Ensure the result has 0x prefix
        if (typeof result === 'string' && !result.startsWith('0x')) {
          result = '0x' + result;
        }
        
        console.log(`Final signature result (${typeof result}):`, result);
      } catch (signatureError) {
        console.error('Error processing signature request:', signatureError);
        setTransactionError(`Signature failed: ${signatureError.message}`);
        
        // For signature errors, set a null result but don't re-throw
        result = null;
      }
    } else if (currentRequest.type === 'chainSwitch') {
      // Handle chain switch - result is null as the switch happens separately
      result = null;
    } else {
      // Unknown request type
      console.warn(`Unknown request type: ${currentRequest.type}`);
      result = null;
    }
    
    // CRITICAL FIX: Ensure result is NEVER undefined
    // This is the key fix for the error in your screenshot
    if (result === undefined) {
      console.warn('Transaction result is undefined, setting to explicit null');
      result = null;
    }
    
    // Special handling for transaction hashes to ensure proper format
    if (currentRequest.type === 'transaction' && 
        typeof result === 'string' && 
        currentRequest.method === 'eth_sendTransaction') {
      
      // Ensure it has 0x prefix
      if (!result.startsWith('0x')) {
        result = '0x' + result;
      }
      
      // Validate hex format
      if (!/^0x[0-9a-fA-F]+$/.test(result)) {
        console.warn('Invalid hex in result, sanitizing');
        const hexMatch = result.match(/0x[0-9a-fA-F]+/);
        if (hexMatch) {
          result = hexMatch[0];
        } else {
          // If we can't extract a valid hex, use a dummy transaction hash
          console.warn('Cannot extract valid hex, using dummy transaction hash');
          result = '0x0000000000000000000000000000000000000000000000000000000000000000';
        }
      }
    }
    
    // Special delay for sending permit responses
    if (isPermit) {
      console.log('Adding slight delay for PERMIT response to ensure proper order');
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Log exactly what result we're about to send
    console.log(`Final result type: ${typeof result}`);
    console.log(`Final result value:`, result);
    
    // Store current request before approval for debugging
    const requestToLog = { ...currentRequest };
    
    // Use the approveRequest function with strict null checking
    if (requestToLog && requestToLog.id) {
      // One final check to ensure result is never undefined
      if (result === undefined) {
        console.warn('Result is still undefined right before approveRequest, forcing to null');
        result = null;
      }
      
      // Log the final call for debugging
      console.log(`Calling approveRequest with ID ${requestToLog.id} and result:`, 
        result === null ? 'null' : (typeof result === 'object' ? 'object' : result));
      
      await approveRequest(requestToLog.id, result);
    } else {
      console.error('Cannot approve request: missing id');
    }
    
    setIsLoading(false);
    
    // Clear current request after approval
    console.log(`Clearing current request after approval:`, requestToLog);
    setCurrentRequest(null);
    
    // If this was a chain switch request, trigger a chain change
    if (requestToLog && 
        requestToLog.type === 'chainSwitch' && 
        requestToLog.method === 'wallet_switchEthereumChain' && 
        requestToLog.params && 
        requestToLog.params[0] && 
        requestToLog.params[0].chainId) {
      
      const chainId = parseInt(requestToLog.params[0].chainId);
      // This would trigger any chain switching mechanism you have
      if (switchNetwork) {
        if (typeof switchNetwork === 'function') {
          // Find the right chain key if we have an object of networks
          const chainKeys = Object.keys(switchNetwork);
          const targetNetwork = chainKeys.find(key => {
            const network = switchNetwork[key];
            return network && network.chainId === chainId;
          });
          
          if (targetNetwork) {
            console.log(`Switching to network ${targetNetwork} after chain switch request`);
            switchNetwork(targetNetwork);
          } else {
            console.warn(`No matching network found for chainId ${chainId}`);
          }
        } else {
          console.warn('switchNetwork is not a function');
        }
      }
    }
  } catch (error) {
    console.error('Error approving request:', error);
    setTransactionError(`Transaction failed: ${error.message}`);
    
    // Reject the request with the error
    if (currentRequest && currentRequest.id) {
      const requestToLog = { ...currentRequest };
      rejectRequest(currentRequest.id);
      
      // Clear current request after rejection
      console.log(`Clearing current request after error:`, requestToLog);
      setCurrentRequest(null);
    }
    setIsLoading(false);
  }
};
  
  // Handler for transaction/request rejection
  const handleRequestReject = () => {
    if (!currentRequest) return;
    
    console.log(`${origin} rejecting request:`, currentRequest);
    const requestToLog = { ...currentRequest };
    
    // Handle authentication request rejection specially
    if (currentRequest.type === 'authentication') {
      rejectAuthentication(currentRequest);
    } else {
      rejectRequest(currentRequest.id);
    }
    
    // Clear current request after rejection
    console.log(`Clearing current request after rejection:`, requestToLog);
    setCurrentRequest(null);
  };
  
  // Handler for chain switch
  const handleChainSwitch = async (chainKey) => {
    if (!currentRequest || currentRequest.type !== 'chainSwitch') return;
    
    setIsLoading(true);
    try {
      // Switch the network
      if (switchNetwork && typeof switchNetwork === 'function') {
        switchNetwork(chainKey);
      } else if (switchNetwork && typeof switchNetwork === 'object') {
        // Handle case where switchNetwork is an object with chainKey methods
        const switchFunction = switchNetwork[chainKey];
        if (typeof switchFunction === 'function') {
          switchFunction();
        }
      }
      
      // Store current request
      const requestToLog = { ...currentRequest };
      
      // Approve the request with null result (success)
      if (currentRequest.id) {
        await approveRequest(currentRequest.id, null);
      }
      setIsLoading(false);
      
      // Clear current request
      console.log(`Clearing current request after chain switch:`, requestToLog);
      setCurrentRequest(null);
    } catch (error) {
      console.error('Error switching chain:', error);
      
      if (currentRequest && currentRequest.id) {
        const requestToLog = { ...currentRequest };
        rejectRequest(currentRequest.id);
        
        // Clear current request after rejection
        console.log(`Clearing current request after chain switch error:`, requestToLog);
        setCurrentRequest(null);
      }
      
      setIsLoading(false);
    }
  };
  
  return (
    <>
      {/* Connection Modal - Only shown for connections with matching origin */}
      {shouldHandleConnection && (
        <ConnectionModal 
          isOpen={true}
          onClose={handleConnectionReject}
          site={pendingConnection?.url || ''}
          hostname={pendingConnection ? new URL(pendingConnection.url).hostname : ''}
          onApprove={handleConnectionApprove}
          onReject={handleConnectionReject}
          isLoading={isLoading}
          connectionType={pendingConnection?.connectionType || 'injected'}
          securityInfo={pendingConnection?.securityInfo}
        />
      )}
      
      {/* Transaction Modal */}
      {currentRequest?.type === 'transaction' && (
        <TransactionModal 
          isOpen={true}
          onClose={handleRequestReject}
          transaction={currentRequest?.params?.[0]} // First param is the transaction
          onApprove={handleTransactionApprove}
          onReject={handleRequestReject}
          isLoading={isLoading}
          site={currentRequest?.url || ''}
          isPermit={currentRequest?.isPermit} // Pass permit flag to transaction modal
          error={transactionError}
        />
      )}
      
      {/* Signature Modal */}
      {currentRequest?.type === 'signature' && (
        <SignatureModal 
          isOpen={true}
          onClose={handleRequestReject}
          signatureRequest={{
            method: currentRequest.method,
            params: currentRequest.params
          }}
          onApprove={handleTransactionApprove}
          onReject={handleRequestReject}
          isLoading={isLoading}
          site={currentRequest?.url || ''}
        />
      )}
      
      {/* Authentication Modal */}
      {currentRequest?.type === 'authentication' && (
        <AuthenticationModal
          isOpen={true}
          onClose={handleRequestReject}
          authRequest={currentRequest}
          onApprove={handleTransactionApprove}
          onReject={handleRequestReject}
          isLoading={isLoading}
          site={currentRequest?.url || ''}
          securityInfo={currentRequest?.securityInfo}
        />
      )}
      
      {/* Chain Switch Modal */}
      {currentRequest?.type === 'chainSwitch' && (
        <ChainSwitchModal 
          isOpen={true}
          onClose={handleRequestReject}
          chainId={parseInt(currentRequest?.params?.[0]?.chainId || '0x1')}
          onApprove={handleChainSwitch}
          onReject={handleRequestReject}
          isLoading={isLoading}
          site={currentRequest?.url || ''}
        />
      )}
    </>
  );
};

export default RequestModalController;